import * as THREE from 'three';
import type { GameMap } from '@sunderfen/shared';
import type { Look } from './look.js';

/**
 * Stylised water, all in one pass with no reflection render:
 *
 *  - depth from a tiny texture of the terrain heights drives everything: wave height (calm at the
 *    shore, rolling offshore), colour absorption (clear turquoise shallows to inky deeps) and opacity
 *  - layered normals: four analytic swells plus two scrolling noise octaves
 *  - sky reflection by fresnel, sampled from the same gradient the sky dome draws
 *  - sun or moon specular with a sharp core, a broad sheen and glitter sparkles
 *  - light glowing through wave crests in the shallows
 *  - shore foam that laps in bands toward the beach, broken up by noise; whitecaps far out at sea
 */
export class Water {
  readonly mesh: THREE.Mesh;
  readonly uniforms = {
    uTime: { value: 0 },
    uDepth: { value: null as unknown as THREE.DataTexture },
    uMapSize: { value: 128 },
    uShallow: { value: new THREE.Color() },
    uDeep: { value: new THREE.Color() },
    uFoam: { value: new THREE.Color() },
    uSkyTop: { value: new THREE.Color() },
    uSkyHorizon: { value: new THREE.Color() },
    uSunDir: { value: new THREE.Vector3(0, 1, 0) },
    uSunColor: { value: new THREE.Color() },
    uFogColor: { value: new THREE.Color() },
    uFogNear: { value: 50 },
    uFogFar: { value: 125 },
    uNight: { value: 0 },
  };

  constructor(map: GameMap, look: Look, segments: number) {
    const VW = map.width + 1, VH = map.height + 1;
    const data = new Uint8Array(VW * VH);
    for (let z = 0; z < VH; z++) for (let x = 0; x < VW; x++) data[x + z * VW] = Math.round(Math.min(1, Math.max(0, -map.vertexHeight(x, z) / 3)) * 255);
    const tex = new THREE.DataTexture(data, VW, VH, THREE.RedFormat, THREE.UnsignedByteType);
    tex.magFilter = THREE.LinearFilter; tex.minFilter = THREE.LinearFilter;
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.needsUpdate = true;
    const u = this.uniforms;
    u.uDepth.value = tex;
    u.uMapSize.value = map.width;
    u.uShallow.value.set(look.water.shallow); u.uDeep.value.set(look.water.deep); u.uFoam.value.set(look.water.foam);

    const common = /* glsl */ `
      uniform sampler2D uDepth;
      uniform float uTime, uMapSize;
      float depthAt( vec2 xz ) {
        vec2 uv = ( xz + 0.5 ) / ( uMapSize + 1.0 );
        float inside = step( 0.0, uv.x ) * step( uv.x, 1.0 ) * step( 0.0, uv.y ) * step( uv.y, 1.0 );
        return mix( 3.0, texture2D( uDepth, clamp( uv, 0.0, 1.0 ) ).r * 3.0, inside );
      }
      // four travelling swells: returns height, d/dx, d/dz
      vec3 swell( vec2 p, float t, float amp ) {
        vec3 r = vec3( 0.0 );
        vec2 d[4]; d[0] = vec2( 0.8, 0.6 ); d[1] = vec2( -0.5, 0.86 ); d[2] = vec2( 0.2, -0.98 ); d[3] = vec2( 0.95, 0.3 );
        float f[4]; f[0] = 0.55; f[1] = 0.9; f[2] = 1.7; f[3] = 3.1;
        float a[4]; a[0] = 0.07; a[1] = 0.045; a[2] = 0.025; a[3] = 0.012;
        float s[4]; s[0] = 0.9; s[1] = 1.25; s[2] = 1.8; s[3] = 2.6;
        for ( int i = 0; i < 4; i++ ) {
          float ph = dot( p, d[i] ) * f[i] + t * s[i];
          r.x += sin( ph ) * a[i] * amp;
          r.yz += d[i] * cos( ph ) * a[i] * f[i] * amp;
        }
        return r;
      }`;

    const geo = new THREE.PlaneGeometry(map.width + 90, map.height + 80, segments, segments);
    geo.rotateX(-Math.PI / 2);
    const mat = new THREE.ShaderMaterial({
      uniforms: u, transparent: true, depthWrite: false,
      vertexShader: /* glsl */ `
        ${common}
        varying vec3 vWorld;
        varying float vDepth;
        varying float vCrest;
        void main() {
          vec4 w = modelMatrix * vec4( position, 1.0 );
          float depth = depthAt( w.xz );
          float amp = 0.35 + smoothstep( 0.1, 2.2, depth ) * 1.4;
          vec3 s = swell( w.xz, uTime, amp );
          w.y += s.x;
          vWorld = w.xyz; vDepth = depth; vCrest = s.x / ( 0.15 * amp + 0.001 );
          gl_Position = projectionMatrix * viewMatrix * w;
        }`,
      fragmentShader: /* glsl */ `
        ${common}
        uniform float uFogNear, uFogFar, uNight;
        uniform vec3 uShallow, uDeep, uFoam, uSkyTop, uSkyHorizon, uSunDir, uSunColor, uFogColor;
        varying vec3 vWorld;
        varying float vDepth;
        varying float vCrest;
        float h21( vec2 p ) { return fract( sin( dot( p, vec2( 127.1, 311.7 ) ) ) * 43758.5453 ); }
        float vnoise( vec2 p ) {
          vec2 i = floor( p ), f = fract( p ); vec2 q = f * f * ( 3.0 - 2.0 * f );
          return mix( mix( h21( i ), h21( i + vec2( 1, 0 ) ), q.x ), mix( h21( i + vec2( 0, 1 ) ), h21( i + vec2( 1, 1 ) ), q.x ), q.y );
        }
        vec2 noiseGrad( vec2 p ) { float e = 0.08; float c = vnoise( p ); return vec2( vnoise( p + vec2( e, 0 ) ) - c, vnoise( p + vec2( 0, e ) ) - c ) / e; }
        void main() {
          float depth = depthAt( vWorld.xz );
          float amp = 0.35 + smoothstep( 0.1, 2.2, depth ) * 1.4;
          vec3 s = swell( vWorld.xz, uTime, amp );
          vec2 g = s.yz;
          g += noiseGrad( vWorld.xz * 1.6 + uTime * vec2( 0.35, 0.22 ) ) * 0.045;
          g += noiseGrad( vWorld.xz * 3.7 - uTime * vec2( 0.27, 0.41 ) ) * 0.022;
          vec3 N = normalize( vec3( -g.x, 1.0, -g.y ) );
          vec3 V = normalize( cameraPosition - vWorld );
          vec3 L = normalize( uSunDir );
          // body colour by absorption
          float absorb = 1.0 - exp( -depth * 1.25 );
          vec3 col = mix( uShallow, uDeep, absorb );
          col *= 0.92 + 0.12 * vnoise( vWorld.xz * 0.35 + uTime * 0.05 );
          // water has no light of its own: darken the body at night (the sky reflection does the rest)
          col *= mix( 1.0, 0.22, uNight );
          // light through crests in the shallows
          float crest = clamp( vCrest, 0.0, 1.5 );
          col += uShallow * crest * 0.18 * ( 1.0 - absorb * 0.6 ) * ( 0.4 + 0.6 * max( dot( V, -L ), 0.0 ) ) * ( 1.0 - uNight * 0.7 );
          // sky reflection
          vec3 R = reflect( -V, N );
          vec3 sky = mix( uSkyHorizon, uSkyTop, pow( clamp( R.y, 0.0, 1.0 ), 0.55 ) );
          float fres = 0.04 + 0.96 * pow( 1.0 - max( dot( N, V ), 0.0 ), 5.0 );
          col = mix( col, sky, clamp( fres * 0.85, 0.0, 0.85 ) );
          // specular: sharp core, broad sheen, glitter
          vec3 H = normalize( L + V );
          float nh = max( dot( N, H ), 0.0 );
          float glitter = step( 0.93, vnoise( vWorld.xz * 9.0 + uTime * 1.7 ) ) * pow( nh, 40.0 );
          col += uSunColor * ( pow( nh, 600.0 ) * 3.0 + pow( nh, 70.0 ) * 0.35 + glitter * 2.5 ) * ( 1.0 - uNight * 0.55 );
          // shore foam lapping inward, broken up by noise
          float n = vnoise( vWorld.xz * 2.2 + uTime * 0.25 );
          float edge = 1.0 - smoothstep( 0.02, 0.18 + n * 0.08, depth );
          float lap = smoothstep( 0.72, 1.0, sin( depth * 16.0 - uTime * 1.6 + n * 2.5 ) ) * ( 1.0 - smoothstep( 0.06, 0.65, depth ) );
          lap *= smoothstep( 0.35, 0.65, vnoise( vWorld.xz * 1.3 - uTime * 0.1 ) );
          float caps = smoothstep( 0.92, 1.3, vCrest ) * smoothstep( 1.8, 2.8, depth ) * smoothstep( 0.45, 0.8, n );
          float foam = clamp( edge * 0.95 + lap * 0.75 + caps * 0.6, 0.0, 1.0 );
          col = mix( col, uFoam * ( 0.92 - uNight * 0.5 ), foam );
          float alpha = clamp( max( 0.3 + absorb * 0.68, foam ), 0.0, 0.97 );
          alpha = max( alpha, fres * 0.6 );
          float f = smoothstep( uFogNear, uFogFar, length( cameraPosition - vWorld ) );
          col = mix( col, uFogColor, f );
          gl_FragColor = vec4( col, alpha );
          #include <tonemapping_fragment>
          #include <colorspace_fragment>
        }`,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.position.set(map.width / 2, 0, map.height / 2 + 25);
    this.mesh.renderOrder = 1;
  }
}
